import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.category === comic.category)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
            <p className="mt-4 text-foreground-500 text-lg">漫画不存在或已下架</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category?.label || comic.categoryLabel, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return String(views);
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    image: comic.cover,
    url: `https://manwacomics.com/comic/${comic.id}`,
    genre: comic.tags,
    inLanguage: 'zh-CN',
    datePublished: comic.createdAt,
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={articleSchema} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <article className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-56 md:w-64 aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - ${comic.categoryLabel}漫画`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="text-sm text-foreground-500">
                  <i className="ri-user-line mr-1" />
                  {comic.author}
                </span>
                <span className={`px-3 py-0.5 rounded-full text-xs font-medium ${
                  comic.category === 'kr' ? 'bg-primary-100 text-primary-700' :
                  comic.category === 'jp' ? 'bg-accent-100 text-accent-700' :
                  'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.categoryLabel}
                </span>
                <span className={`px-3 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === 'ongoing'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.statusLabel}
                </span>
              </div>

              <div className="flex items-center gap-5 mt-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="text-lg font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-sm text-foreground-400">/ 5.0</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-eye-line" />
                  <span>{formatViews(comic.views)} 阅读</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-file-list-3-line" />
                  <span>{comic.chapters.length} 话</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 p-4 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button className="px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
                  开始阅读 第1话
                </button>
                <button className="w-11 h-11 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  <i className="ri-heart-line text-lg" />
                </button>
                <button className="w-11 h-11 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  <i className="ri-bookmark-line text-lg" />
                </button>
              </div>
            </div>
          </article>

          <section className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-100 text-primary-700">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-500 hover:bg-background-100 transition-colors">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
              {comic.chapters.slice(0, 50).map((ch) => (
                <button
                  key={ch.id}
                  className="px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-700 text-sm text-foreground-700 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  {ch.title}
                </button>
              ))}
            </div>
            {comic.chapters.length > 50 && (
              <div className="text-center mt-4">
                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  展开全部 {comic.chapters.length} 话 <i className="ri-arrow-down-s-line" />
                </button>
              </div>
            )}
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-bold text-foreground-950">相关推荐</h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  更多{comic.categoryLabel} <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}